import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Plus } from 'lucide-react';
import { acceptQuote, createQuote, getQuoteRequest, listQuoteRequests } from '@/api/stock';
import { ApiError } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { DesktopTable } from '@/components/common/responsiveList';
import type { Quote, QuotePrice, QuoteRequestDetail, QuoteRequestLine } from '@/types/oscar';

/** DD/MM/YYYY from an ISO instant. */
function when(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/**
 * ⛔ NULL IS NOT ZERO. A supplier who did not quote a line has told us
 * something; rendering it as $0.00 would make an incomplete quote read as the
 * cheapest one on the page.
 */
function money(v: number | null): string {
  return v === null ? '—' : v.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
}

/**
 * ⛔ A TOTAL OVER AN INCOMPLETE QUOTE IS NOT A TOTAL. If any line was not
 * quoted, the sum of the rest is not what this supplier would charge — so it
 * is reported as a partial, never as a comparable figure.
 */
function quoteTotal(prices: QuotePrice[], lines: QuoteRequestLine[]):
{ total: number; missing: number } {
  const mine = prices.filter((p) => p.LineTotal !== null);
  return {
    total: mine.reduce((sum, p) => sum + (p.LineTotal ?? 0), 0),
    missing: lines.length - mine.length,
  };
}

const inputCls = 'mt-1 w-full rounded border border-border bg-white px-2 py-1.5 text-sm font-normal';

// ──────────────────────── recording one supplier's answer ───────────────────

function RecordQuoteDialog({ detail, onClose, onSaved }: {
  detail: QuoteRequestDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [supplierName, setSupplierName] = useState('');
  const [leadTimeDays, setLeadTime] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [prices, setPrices] = useState<Record<number, number | ''>>({});
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => createQuote(detail.Request.StockQuoteRequestId, {
      SupplierName: supplierName.trim(),
      LeadTimeDays: leadTimeDays === '' ? null : leadTimeDays,
      Notes: notes.trim() || null,
      // ⛔ A BLANK BOX IS SENT AS null, NOT DROPPED AND NOT ZEROED. "They did
      //    not quote this line" is an answer worth recording.
      Lines: detail.Lines.map((l) => ({
        StockQuoteRequestLineId: l.StockQuoteRequestLineId,
        UnitCost: prices[l.StockQuoteRequestLineId] === '' || prices[l.StockQuoteRequestLineId] === undefined
          ? null
          : Number(prices[l.StockQuoteRequestLineId]),
      })),
    }),
    onSuccess: onSaved,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not record the quote.'),
  });

  return (
    <AlertDialog
      open
      wide
      onOpenChange={(o) => { if (!o) onClose(); }}
      title="Record a quote"
      confirmLabel="Save"
      confirmDisabled={supplierName.trim().length === 0}
      closeOnConfirm={false}
      onConfirm={() => { setError(null); mutation.mutate(); }}
    >
      <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
        {error && <div role="alert" className="rounded border border-red/30 bg-red/10 px-2 py-1 text-xs text-red">{error}</div>}

        <div className="grid gap-2 sm:grid-cols-2">
          <label className="block text-xs font-semibold text-darkGrey">Supplier
            <input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} className={inputCls} />
          </label>
          <label className="block text-xs font-semibold text-darkGrey">Lead time (days)
            <input
              type="number"
              min={0}
              value={leadTimeDays}
              onChange={(e) => setLeadTime(e.target.value === '' ? '' : Math.max(0, Number(e.target.value)))}
              className={inputCls}
            />
          </label>
        </div>

        <p className="text-xs font-normal text-darkGrey/70">
          Unit price excluding GST. Leave a line blank if they did not quote it — that is
          recorded as “not quoted”, not as zero.
        </p>

        <div className="space-y-2">
          {detail.Lines.map((l) => (
            <div key={l.StockQuoteRequestLineId} className="flex items-start gap-2 rounded border border-border bg-bg/40 px-2 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-navy">{l.ItemName}</p>
                <p className="text-xs text-darkGrey/70">
                  {l.Qty} required{l.SupplierPartNumber ? ` · ${l.SupplierPartNumber}` : ''}
                </p>
              </div>
              <label className="w-32 shrink-0 text-xs font-semibold text-darkGrey">
                Unit price
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  aria-label={`Unit price for ${l.ItemName}`}
                  value={prices[l.StockQuoteRequestLineId] ?? ''}
                  onChange={(e) => setPrices({
                    ...prices,
                    [l.StockQuoteRequestLineId]: e.target.value === '' ? '' : Number(e.target.value),
                  })}
                  className={inputCls}
                />
              </label>
            </div>
          ))}
        </div>

        <label className="block text-xs font-semibold text-darkGrey">Notes (internal)
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={inputCls} />
        </label>
      </div>
    </AlertDialog>
  );
}

// ─────────────────────────── one request, compared ──────────────────────────

function RequestDetail({ id, onBack }: { id: number; onBack: () => void }) {
  const { canMutate } = useAuth();
  const queryClient = useQueryClient();
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ['stock-quote-request', id],
    queryFn: ({ signal }) => getQuoteRequest(id, signal),
  });

  const accept = useMutation({
    mutationFn: ({ quoteId, accepted }: { quoteId: number; accepted: boolean }) =>
      acceptQuote(quoteId, accepted),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['stock-quote-request', id] }),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not record that decision.'),
  });

  if (detail.isLoading) return <p className="py-6 text-center text-sm text-darkGrey/60">Loading…</p>;
  if (detail.isError || !detail.data) {
    return (
      <div className="py-6">
        <p role="alert" className="text-sm text-red">That quote request could not be loaded.</p>
        <button type="button" onClick={onBack} className="mt-2 text-sm font-semibold text-navy hover:underline">
          Back to the list
        </button>
      </div>
    );
  }

  const d = detail.data;
  const pricesFor = (q: Quote) => d.Prices.filter((p) => p.StockQuoteId === q.StockQuoteId);
  const priceAt = (q: Quote, lineId: number) =>
    d.Prices.find((p) => p.StockQuoteId === q.StockQuoteId && p.StockQuoteRequestLineId === lineId);

  return (
    <section>
      <button type="button" onClick={onBack} className="text-sm font-semibold text-navy hover:underline">
        ← All quote requests
      </button>

      <header className="mt-3 mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-navy">
            {d.Request.Reference || `Quote request ${d.Request.StockQuoteRequestId}`}
          </h1>
          <p className="mt-1 text-sm text-darkGrey/80">
            Raised by {d.Request.RaisedBy} on {when(d.Request.RaisedAtUtc)} · {d.Request.LineCount} line
            {d.Request.LineCount === 1 ? '' : 's'}
          </p>
        </div>
        {canMutate && (
          <button
            type="button"
            onClick={() => setRecording(true)}
            className="inline-flex items-center gap-2 rounded bg-orange px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            <Plus className="h-4 w-4" aria-hidden /> Record a quote
          </button>
        )}
      </header>

      {error && <p role="alert" className="mb-3 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">{error}</p>}

      {d.Quotes.length === 0 && (
        <p className="rounded border border-border bg-bg/40 px-3 py-4 text-sm text-darkGrey/70">
          No quotes recorded yet. When a supplier answers, record it here and the prices line up
          beside each other.
        </p>
      )}

      {d.Quotes.length > 0 && (
        <DesktopTable>
          <table className="w-full border-collapse text-sm">
            <thead className="bg-bg text-left text-xs uppercase tracking-wide text-darkGrey/70">
              <tr>
                <th className="px-3 py-2">Item</th>
                <th className="px-3 py-2">Qty</th>
                {d.Quotes.map((q) => (
                  <th key={q.StockQuoteId} className="px-3 py-2">
                    {q.SupplierName}
                    {q.IsAccepted && (
                      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-navy/10 px-2 py-0.5 text-xs font-semibold text-navy">
                        <Check className="h-3 w-3" aria-hidden /> Accepted
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.Lines.map((l) => (
                <tr key={l.StockQuoteRequestLineId} className="border-t border-border">
                  <td className="px-3 py-2 font-medium text-navy">
                    {l.ItemName}
                    {l.SupplierPartNumber && (
                      <span className="block text-xs font-normal text-darkGrey/70">{l.SupplierPartNumber}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">{l.Qty}</td>
                  {d.Quotes.map((q) => {
                    const p = priceAt(q, l.StockQuoteRequestLineId);
                    return (
                      <td key={q.StockQuoteId} className="px-3 py-2">
                        {/* ⛔ "not quoted" is said in words, never as $0.00. */}
                        {p?.UnitCost == null
                          ? <span className="text-darkGrey/60">not quoted</span>
                          : money(p.UnitCost)}
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr className="border-t-2 border-navy/30 bg-lightBlue/20 font-semibold">
                <td className="px-3 py-2" colSpan={2}>Total</td>
                {d.Quotes.map((q) => {
                  const { total, missing } = quoteTotal(pricesFor(q), d.Lines);
                  return (
                    <td key={q.StockQuoteId} className="px-3 py-2">
                      {money(total)}
                      {/* ⛔ A TOTAL OVER AN INCOMPLETE QUOTE IS NOT COMPARABLE,
                          and saying so is the difference between a comparison
                          and a wrong decision. */}
                      {missing > 0 && (
                        <span className="block text-xs font-normal text-orange">
                          {missing} line{missing === 1 ? '' : 's'} not quoted — not comparable
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
              <tr className="border-t border-border">
                <td className="px-3 py-2 text-xs uppercase tracking-wide text-darkGrey/70" colSpan={2}>
                  Lead time
                </td>
                {d.Quotes.map((q) => (
                  <td key={q.StockQuoteId} className="px-3 py-2 text-sm">
                    {q.LeadTimeDays === null ? '—' : `${q.LeadTimeDays} days`}
                  </td>
                ))}
              </tr>
              {canMutate && (
                <tr className="border-t border-border">
                  <td className="px-3 py-2" colSpan={2} />
                  {d.Quotes.map((q) => (
                    <td key={q.StockQuoteId} className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => { setError(null); accept.mutate({ quoteId: q.StockQuoteId, accepted: !q.IsAccepted }); }}
                        disabled={accept.isPending}
                        className={`rounded px-2 py-1 text-xs font-semibold disabled:opacity-50 ${
                          q.IsAccepted
                            ? 'border border-border text-darkGrey hover:bg-bg'
                            : 'bg-navy text-white hover:opacity-90'}`}
                      >
                        {q.IsAccepted ? 'Stand down' : 'Accept this one'}
                      </button>
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </DesktopTable>
      )}

      {d.Request.AcceptedSupplierName && (
        <p className="mt-4 rounded border border-navy/30 bg-lightBlue/30 px-3 py-2 text-sm text-navy">
          <strong>{d.Request.AcceptedSupplierName}</strong> accepted on {when(d.Request.AcceptedAtUtc)}.
          {/* ⚠ Says what accepting does NOT do, so nobody waits for a purchase
              order that was never going to appear. */}
          <span className="mt-0.5 block text-xs font-normal text-darkGrey/80">
            Recorded here only — this does not raise a purchase or change the stock line’s unit cost.
          </span>
        </p>
      )}

      {recording && (
        <RecordQuoteDialog
          detail={d}
          onClose={() => setRecording(false)}
          onSaved={() => {
            setRecording(false);
            void queryClient.invalidateQueries({ queryKey: ['stock-quote-request', id] });
          }}
        />
      )}
    </section>
  );
}

// ───────────────────────────── the list ─────────────────────────────────────

/**
 * Quote requests, and the answers that came back (107).
 *
 * <p>Anthony, 13/10/2026: <i>"now do option c so quotes come back in."</i></p>
 *
 * ⚠ ONE ROUTE, not two. The detail renders in place rather than at
 * /stock/quotes/:id, which keeps the URL surface to the single route that was
 * agreed. The cost is that a particular request cannot be linked to — worth
 * revisiting if people start sending each other links.
 */
export function StockQuotesPage() {
  const [openId, setOpenId] = useState<number | null>(null);

  const query = useQuery({
    queryKey: ['stock-quote-requests'],
    queryFn: ({ signal }) => listQuoteRequests(signal),
  });

  if (openId !== null) return <RequestDetail id={openId} onBack={() => setOpenId(null)} />;

  const rows = query.data?.Requests ?? [];

  return (
    <section>
      <header className="mb-5">
        <h1 className="font-heading text-2xl font-bold text-navy">Quote requests</h1>
        <p className="mt-1 text-sm text-darkGrey/80">
          What was asked, and what each supplier answered. Raise one by picking lines on the
          Stock screen.
        </p>
      </header>

      {query.isLoading && <p className="py-6 text-center text-sm text-darkGrey/60">Loading…</p>}

      {query.isError && (
        <p role="alert" className="rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">
          Quote requests could not be loaded.{' '}
          <button type="button" onClick={() => void query.refetch()} className="font-semibold underline">
            Try again
          </button>
        </p>
      )}

      {query.data && rows.length === 0 && (
        <p className="rounded border border-border bg-bg/40 px-3 py-4 text-sm text-darkGrey/70">
          Nothing yet. Tick lines on the Stock screen and press <strong>Request a quote</strong>.
        </p>
      )}

      {rows.length > 0 && (
        <DesktopTable>
          <table className="w-full border-collapse text-sm">
            <thead className="bg-bg text-left text-xs uppercase tracking-wide text-darkGrey/70">
              <tr>
                <th className="px-3 py-2">Reference</th>
                <th className="px-3 py-2">Raised</th>
                <th className="px-3 py-2">Lines</th>
                <th className="px-3 py-2">Quotes</th>
                <th className="px-3 py-2">Accepted</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.StockQuoteRequestId} className="border-t border-border hover:bg-bg/60">
                  <td className="px-3 py-2 font-medium text-navy">
                    {r.Reference || `Request ${r.StockQuoteRequestId}`}
                  </td>
                  <td className="px-3 py-2 text-darkGrey/80">
                    {when(r.RaisedAtUtc)}
                    <span className="block text-xs">{r.RaisedBy}</span>
                  </td>
                  <td className="px-3 py-2">{r.LineCount}</td>
                  <td className="px-3 py-2">
                    {r.QuoteCount}
                    {/* ⚠ A request with no answers is the one that needs
                        chasing, so it says so rather than showing a bare 0. */}
                    {r.QuoteCount === 0 && (
                      <span className="ml-2 text-xs font-normal text-orange">none back yet</span>
                    )}
                  </td>
                  <td className="px-3 py-2">{r.AcceptedSupplierName ?? '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      aria-label={`Open ${r.Reference || `request ${r.StockQuoteRequestId}`}`}
                      onClick={() => setOpenId(r.StockQuoteRequestId)}
                      className="text-xs font-semibold text-navy hover:underline"
                    >
                      Open…
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DesktopTable>
      )}
    </section>
  );
}
